import { useState } from "react";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
} from "@heroui/react";
import { KeyRound, Trash2, ShieldCheck, AlertTriangle } from "lucide-react";
import {
  browserSupportsWebAuthn,
  startRegistration,
  WebAuthnError,
} from "@simplewebauthn/browser";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { api, queryClient } from "@/lib/api-client";
import { sentryLog } from "@/utils/sentry-utils";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";

type PasskeysModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type PendingAction = { type: "add" } | { type: "delete"; credentialId: number };

type StepUpProof = { token: string; expiresAt: number };

function formatDate(value: string | Date | null | undefined) {
  if (!value) return null;
  return new Date(value).toLocaleDateString();
}

function isLiveProof(proof: StepUpProof | null): proof is StepUpProof {
  return proof !== null && proof.expiresAt > Date.now();
}

function readServerError(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const response = (error as { response?: { data?: { error?: unknown } } })
    .response;
  return typeof response?.data?.error === "string"
    ? response.data.error
    : undefined;
}

export const PasskeysModal = ({ isOpen, onClose }: PasskeysModalProps) => {
  const [name, setName] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [password, setPassword] = useState("");
  const [proof, setProof] = useState<StepUpProof | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const supported = browserSupportsWebAuthn();

  const { data, isLoading } = api.webauthn.useWebauthnCredentials(
    isOpen && supported,
  );

  const invalidatePasskeyState = () => {
    queryClient.invalidateQueries({ queryKey: ["webauthn-credentials"] });
    queryClient.invalidateQueries({ queryKey: ["mfa-status"] });
  };
  const { mutateAsync: getRegisterOptions } =
    api.webauthn.useWebauthnRegisterOptions();
  const { mutateAsync: verifyRegistration } =
    api.webauthn.useWebauthnRegisterVerify();
  const { mutateAsync: deleteCredential } =
    api.webauthn.useWebauthnDeleteCredential();
  const { mutateAsync: stepUp, isPending: isAuthorizing } =
    api.auth.useAuthStepUp();

  const credentials = data?.credentials ?? [];

  const runAdd = async (stepUpToken: string) => {
    setIsRegistering(true);
    try {
      const { options, registrationToken } = await getRegisterOptions();
      const attestation = await startRegistration({ optionsJSON: options });
      const result = await verifyRegistration({
        registrationToken,
        response: attestation,
        name: name.trim() || undefined,
        stepUpToken,
      });

      // Only returned for a first passkey on an account with no authenticator
      // app: these are the only way back in from a device without the passkey.
      if (result.recoveryCodes) {
        setRecoveryCodes(result.recoveryCodes);
      }

      addToast({
        title: t`Passkey added`,
        description: t`You can now sign in with this passkey.`,
        color: "success",
      });
      setName("");
      invalidatePasskeyState();
    } catch (error) {
      // A dismissed OS prompt and an unavailable authenticator both surface as
      // NotAllowedError, so this cannot be reported as a silent cancellation.
      const aborted =
        error instanceof WebAuthnError &&
        error.code === "ERROR_CEREMONY_ABORTED";

      if (!aborted) {
        sentryLog.error("Passkey registration failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        addToast({
          title: t`Could not add passkey`,
          description:
            readServerError(error) ??
            t`The passkey prompt was dismissed or timed out. Please try again.`,
          color: "danger",
        });
      }
    }
    setIsRegistering(false);
  };

  const runDelete = async (credentialId: number, stepUpToken: string) => {
    setDeletingId(credentialId);
    try {
      await deleteCredential({ credentialId, stepUpToken });
      invalidatePasskeyState();
      addToast({ title: t`Passkey removed`, color: "success" });
    } catch (error) {
      sentryLog.error("Passkey removal failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      addToast({
        title: t`Could not remove passkey`,
        description: readServerError(error) ?? t`Please try again.`,
        color: "danger",
      });
    }
    setDeletingId(null);
  };

  const runAction = async (action: PendingAction, stepUpToken: string) => {
    if (action.type === "add") {
      await runAdd(stepUpToken);
      return;
    }
    await runDelete(action.credentialId, stepUpToken);
  };

  const requestAction = async (action: PendingAction) => {
    if (isLiveProof(proof)) {
      await runAction(action, proof.token);
      return;
    }
    setPendingAction(action);
  };

  const handleAuthorize = async () => {
    if (!pendingAction || password.length === 0) return;

    const authorized = await (async () => {
      try {
        return await stepUp({ password });
      } catch (error) {
        addToast({
          title: t`Password is incorrect`,
          description: readServerError(error) ?? t`Please try again.`,
          color: "danger",
        });
        return null;
      }
    })();

    if (!authorized) return;

    setProof({
      token: authorized.stepUpToken,
      expiresAt: Date.now() + authorized.expiresInSeconds * 1000,
    });
    setPassword("");
    const action = pendingAction;
    setPendingAction(null);
    await runAction(action, authorized.stepUpToken);
  };

  const handleClose = () => {
    setName("");
    setPassword("");
    setPendingAction(null);
    setProof(null);
    setRecoveryCodes(null);
    onClose();
  };

  const isBusy = isRegistering || deletingId !== null;

  return (
    <LimeModal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <KeyRound className="text-primary" size={20} />
          <Trans>Passkeys</Trans>
        </ModalHeader>
        <ModalBody>
          <p className="text-default-600 text-sm">
            <Trans>
              Sign in with your fingerprint, face, screen lock, or a security
              key instead of typing a code every time.
            </Trans>
          </p>

          {!supported && (
            <p className="text-warning-700 text-sm">
              <Trans>
                This browser does not support passkeys. Try a different browser
                or device.
              </Trans>
            </p>
          )}

          {recoveryCodes && (
            <div className="border-warning-200 bg-warning-50 flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-warning shrink-0" size={16} />
                <p className="text-warning-800 text-sm font-medium">
                  <Trans>Save your recovery codes</Trans>
                </p>
              </div>
              <p className="text-warning-700 text-xs">
                <Trans>
                  These are shown once. Without your passkey, they are the only
                  way back in — store them somewhere safe.
                </Trans>
              </p>
              <div className="grid grid-cols-2 gap-1 font-mono text-xs">
                {recoveryCodes.map((code) => (
                  <span key={code} className="text-warning-900">
                    {code}
                  </span>
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => setRecoveryCodes(null)}
                >
                  <Trans>I have saved them</Trans>
                </Button>
              </div>
            </div>
          )}

          {supported && pendingAction && (
            <div className="border-default-200 bg-default-50 flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-primary shrink-0" size={16} />
                <p className="text-sm font-medium">
                  {pendingAction.type === "add" ? (
                    <Trans>Confirm your password to add a passkey</Trans>
                  ) : (
                    <Trans>Confirm your password to remove this passkey</Trans>
                  )}
                </p>
              </div>
              <LimeInput
                label={t`Password`}
                type="password"
                size="sm"
                autoFocus
                className="w-full"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => {
                    setPendingAction(null);
                    setPassword("");
                  }}
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  isLoading={isAuthorizing}
                  isDisabled={password.length === 0}
                  onPress={handleAuthorize}
                >
                  <Trans>Confirm</Trans>
                </Button>
              </div>
            </div>
          )}

          {supported && (
            <>
              <div className="flex flex-col gap-2">
                {isLoading ? (
                  <p className="text-default-500 text-sm">
                    <Trans>Loading passkeys…</Trans>
                  </p>
                ) : credentials.length === 0 ? (
                  <p className="text-default-500 text-sm">
                    <Trans>No passkeys yet.</Trans>
                  </p>
                ) : (
                  credentials.map((credential) => {
                    const created = formatDate(credential.createdAt);
                    const lastUsed = formatDate(credential.lastUsedAt);
                    return (
                      <div
                        key={credential.id}
                        className="border-default-200 flex items-center justify-between gap-2 rounded-lg border p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {credential.name || t`Passkey`}
                          </p>
                          <p className="text-default-400 truncate text-xs">
                            {lastUsed
                              ? t`Last used ${lastUsed}`
                              : created
                                ? t`Added ${created}`
                                : null}
                          </p>
                        </div>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          color="danger"
                          aria-label={t`Remove passkey`}
                          isLoading={deletingId === credential.id}
                          isDisabled={isBusy && deletingId !== credential.id}
                          onPress={() =>
                            requestAction({
                              type: "delete",
                              credentialId: credential.id,
                            })
                          }
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>

              <LimeInput
                label={t`Passkey name (optional)`}
                size="sm"
                className="mt-1 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t`e.g. Work MacBook`}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={handleClose}>
            <Trans>Close</Trans>
          </Button>
          <Button
            color="primary"
            startContent={<KeyRound size={16} />}
            isLoading={isRegistering}
            isDisabled={!supported || isBusy}
            onPress={() => requestAction({ type: "add" })}
          >
            <Trans>Add a passkey</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
};
